import React, { useCallback, useEffect, useState } from 'react';
import { fetchMcpKeys, createMcpKey, revokeMcpKey } from '../services/mcpKeysService';
import Button from './Button';
import './McpKeysPanel.css';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const CopyField = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      /* selection fallback below */
    }
  };
  return (
    <div className="mcp-copy">
      <span className="mcp-copy-label">{label}</span>
      <code className="mcp-copy-value" onClick={(e) => { const r = document.createRange(); r.selectNodeContents(e.currentTarget); window.getSelection().removeAllRanges(); window.getSelection().addRange(r); }}>{value}</code>
      <button type="button" className="mcp-copy-btn" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
    </div>
  );
};

/**
 * Mint, list and revoke API keys for the REKKIE MCP endpoint. Lives on the
 * Settings tab: access is any @rekkie.com account, not a product.
 */
const McpKeysPanel = () => {
  const [keys, setKeys] = useState([]);
  const [mcpUrl, setMcpUrl] = useState('');
  const [label, setLabel] = useState('');
  const [minted, setMinted] = useState(null); // { secret, key }
  const [confirmRevoke, setConfirmRevoke] = useState(null); // keyHash
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMcpKeys();
      setKeys(data.keys || []);
      setMcpUrl(data.mcpUrl || '');
    } catch (e) {
      if (e.status === 403) setForbidden(true); else setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await createMcpKey(label.trim());
      setMinted({ secret: data.secret, key: data.key });
      setLabel('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (keyHash) => {
    setBusy(true);
    setError(null);
    try {
      await revokeMcpKey(keyHash);
      setConfirmRevoke(null);
      if (minted?.key?.keyHash === keyHash) setMinted(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (forbidden) {
    return (
      <section className="mcp-keys">
        <h3>REKKIE MCP API keys</h3>
        <p className="mcp-muted">Available to @rekkie.com accounts.</p>
      </section>
    );
  }

  const active = keys.filter((k) => !k.revokedAt);
  const revoked = keys.filter((k) => k.revokedAt);
  const addCommand = minted
    ? `claude mcp add --transport http rekkie ${mcpUrl} --header "Authorization: Bearer ${minted.secret}"`
    : null;

  return (
    <section className="mcp-keys">
      <h3>REKKIE MCP API keys</h3>
      <p className="mcp-muted">
        Keys let an AI agent (Claude Code, Claude Desktop, any MCP client) read per-user analytics, crashes, ski days
        and the release register through <code>{mcpUrl || '/mcp'}</code>. A key acts as you and can be revoked here.
      </p>

      {error && <div className="mcp-error">{error}</div>}

      <form className="mcp-create" onSubmit={create}>
        <input
          id="mcp-key-label"
          type="text"
          placeholder="Label, e.g. laptop claude code"
          value={label}
          maxLength={80}
          onChange={(e) => setLabel(e.target.value)}
          disabled={busy}
        />
        <Button variant="primary" type="submit" disabled={busy}>Create key</Button>
      </form>

      {minted && (
        <div className="mcp-minted">
          <div className="mcp-minted-title">New key{minted.key.label ? ` “${minted.key.label}”` : ''}. Copy it now; it is not shown again.</div>
          <CopyField label="Key" value={minted.secret} />
          <CopyField label="Claude Code" value={addCommand} />
          <div className="mcp-minted-actions">
            <button type="button" className="mcp-link" onClick={() => setMinted(null)}>Done</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="mcp-muted">Loading…</p>
      ) : (
        <>
          <table className="mcp-table">
            <thead>
              <tr><th>Key</th><th>Label</th><th>Created</th><th>Last used</th><th></th></tr>
            </thead>
            <tbody>
              {active.length === 0 && (
                <tr><td colSpan={5} className="mcp-muted">No active keys.</td></tr>
              )}
              {active.map((k) => (
                <tr key={k.keyHash}>
                  <td><code>{k.prefix}…</code></td>
                  <td>{k.label || <span className="mcp-muted">—</span>}</td>
                  <td>{fmt(k.createdAt)}</td>
                  <td>{fmt(k.lastUsedAt)}</td>
                  <td className="mcp-actions">
                    {confirmRevoke === k.keyHash ? (
                      <>
                        <button type="button" className="mcp-link mcp-danger" disabled={busy} onClick={() => revoke(k.keyHash)}>Confirm revoke</button>
                        <button type="button" className="mcp-link" disabled={busy} onClick={() => setConfirmRevoke(null)}>Cancel</button>
                      </>
                    ) : (
                      <button type="button" className="mcp-link" disabled={busy} onClick={() => setConfirmRevoke(k.keyHash)}>Revoke</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {revoked.length > 0 && (
            <p className="mcp-muted mcp-revoked">{revoked.length} revoked key{revoked.length === 1 ? '' : 's'} not shown.</p>
          )}
        </>
      )}
    </section>
  );
};

export default McpKeysPanel;
